import { AuthState } from "@okta/okta-auth-js";
import classNames from "classnames";
import * as React from "react";
import { connect } from "react-redux";
import { numberOfEmptyRows, responseListDefaultSort } from "../../AppConstants";
import appHistory from "../../AppHistory";
import i18n from "../../i18n";
import { EmptyListModal } from "../EmptyListModal/EmptyListModal";
import Pagination from "../Pagination/Index";
import { SkeletonTableRow } from "../SkeletonTableRow";
import { ResponseListRow } from "./ResponseListRow";
import { HistoricalResponseListModal } from "../../../../src/ts/Responses/components/HistoricalResponseListModal";
import { Tooltip } from "react-tooltip";
import "./Responses.scss";
import { TextFilter } from "../../Analytics/components/Filters/TextFilter";
import { NumberFilter } from "../../Analytics/components/Filters/NumberFilter";
import { SelectFilter } from "../../Analytics/components/Filters/SelectFilter";
import { DateFilter } from "../../Analytics/components/Filters/DateFilter";
import { IStoreState } from "../../Model";
import { setFilter, clearFilter, clearAllFilters } from "../../../../src/ts/Responses/duck/Actions";
import { IFilter } from "../../Responses/duck/Models";

export interface IResponseListProps {
  list: Api.IPagedResult<Api.IResponseListViewModel>;
  baseRoute: string;
  isDraftList: boolean;
  authState: AuthState | null;
  handlePageSizeChange: (
    pageNumber: number,
    itemPerPage: number,
    auth: AuthState | null,
    sortOrder?: string
  ) => any;
  onSortChange?: (orderBy: string) => void;
  orderBy?: string;
  filters: IFilter[];
  filteredResults: Api.IResponseListViewModel[];
  setFilter: (field: string, value: any, operator?: string) => void;
  clearFilter: (field: string) => void;
  clearAllFilters: () => void;
  onFacetChange?: (propertyName: string) => Promise<any>;
}

interface IResponsesState {
  openModalState: boolean;
  responseId: string;
  documentId: string;
}

class Responses extends React.Component<IResponseListProps, IResponsesState> {
  constructor(props: IResponseListProps) {
    super(props);

    this.state = {
      openModalState: false,
      responseId: "",
      documentId: ""
    };

    this.sortOrderChanged = this.sortOrderChanged.bind(this);
    this.handlePageSizeChange = this.handlePageSizeChange.bind(this);
    this.handleVersionIconClick = this.handleVersionIconClick.bind(this);
    this.handleCloseIconClick = this.handleCloseIconClick.bind(this);
    this.handleClickHeader = this.handleClickHeader.bind(this);
    this.handleFilterApply = this.handleFilterApply.bind(this);
    this.handleFilterClear = this.handleFilterClear.bind(this);
    this.getCategoryOptions = this.getCategoryOptions.bind(this);
  }

  // Helper function to get filter value from array (same pattern as Analytics)
  private getFilterValue(field: string, operator?: string): any {
    const filter = this.props.filters.find((f: IFilter) =>
      f.field === field && (operator ? f.operator === operator : true)
    );
    return filter?.value;
  }

  // Helper function to check if any filter is active for a field
  private isFilterActive(field: string): boolean {
    return this.props.filters.some((f: IFilter) => f.field === field);
  }


  private handleFilterApply(field: string, value: any, operator: string = "eq", hasSecondFilter: boolean = false) {

    const finalOperator = field === 'title' && operator === 'eq' ? 'contains' : operator;
    this.props.setFilter(field, value, finalOperator);
    // Update URL to page 1 using replace to avoid adding to history
    // This ensures the URL reflects the current state
    const newRoute = {
      ...appHistory.location,
      pathname: `${this.props.baseRoute}/1`
    };
    appHistory.replace(newRoute);
  }

  private handleFilterClear(field: string) {
    this.props.clearFilter(field);
    // Update URL to page 1 using replace to avoid adding to history
    // This ensures the URL reflects the current state
    const newRoute = {
      ...appHistory.location,
      pathname: `${this.props.baseRoute}/1`
    };
    appHistory.replace(newRoute);
  }

  private getCategoryOptions() {
    if (this.props.onFacetChange) {
      // Use the facet API to get categories
      return this.props.onFacetChange('category/name')
        .then((response: any) => {
          return response;
        })
        .catch((error: any) => {
          console.error(error);
          // Fallback to extracting from data if API call fails
          const categories = Array.from(new Set(
            this.props.list.results.map(item => item.category)
          ));
          return { facets: [{ groups: categories.map(cat => ({ key: cat })) }] };
        });
    } else {
      // Fallback to extracting from data if onFacetChange is not provided
      const categories = Array.from(new Set(
        this.props.list.results.map(item => item.category)
      ));
      return Promise.resolve({ facets: [{ groups: categories.map(cat => ({ key: cat })) }] });
    }
  }

  public render() {
    const clsName =
      this.props.list.sortOrder && this.props.list.sortOrder.indexOf("-") > -1
        ? "fa fa-sort-amount-down"
        : "fa fa-sort-amount-up";
    const isSkeleton: boolean =
      this.props.list.results && this.props.list.rowCount === 0;

    const displayResults = !this.props.isDraftList && this.props.filteredResults.length > 0 ? this.props.filteredResults : this.props.list.results;
    // Check if the current sorting is on dateModified (default sorting)
    const isDefaultSorting = !this.props.orderBy || this.props.orderBy.startsWith('dateModified');
    
    // Determine if filters are active (only for published list, not drafts)
    const hasActiveFilters = !this.props.isDraftList && this.props.filters && this.props.filters.length > 0;
    
    // Show empty modal only when there are truly no responses AND no filters are active
    const showEmptyModal = isSkeleton && !hasActiveFilters;
    
    // Check if we should show "No results found"
    const showNoResults = (displayResults.length === 0 && hasActiveFilters) || (!isSkeleton && displayResults.length === 0);

    return (
      <div className={`${!this.props.isDraftList ? 'new-table-margin-top published-responses' : 'drafts-responses'} `} >
        <table
          className={classNames(
            "table",
            { "table-hover": !isSkeleton },
            "table-responsive",
            { "default-sorting": isDefaultSorting },
            { "no-results": showNoResults }
          )}
          id={this.props.isDraftList ? '' : 'published-table'}
        >
          <thead>
            {this.props.isDraftList ?
              <tr>
                <th className="referenceId" scope="col">
                  {i18n.t("responseList.updatedTitleIdHeading")}
                </th>
                <th className="title" scope="col">
                  {i18n.t("responseList.titleColHeading")}
                </th>
                <th className="category" scope="col">
                  {i18n.t("responseList.categoryHeading")}
                </th>
                <th className="date" scope="col">
                  <span className="date-label">
                    {i18n.t("responseList.updatedDateColHeading")}
                  </span>
                  <i
                    role="sort-icon"
                    className={clsName}
                    onClick={this.sortOrderChanged}
                  />
                </th>
              </tr>
              :
              <tr>
                <th className={classNames("referenceId", { "filter-applied": this.isFilterActive('referenceId') })} scope="col" data-column-index="0" data-property="referenceId">
                  <span className="published-table-heading">
                    {i18n.t("responseList.updatedTitleIdHeading")}<span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                    <NumberFilter
                      field={{ name: 'Response ID', property: 'referenceId' }}
                      firstFilterValue={this.getFilterValue('referenceId', 'ge')}
                      secondFilterValue={this.getFilterValue('referenceId', 'le')}
                      onFilterApply={this.handleFilterApply}
                      onClear={this.handleFilterClear}
                    />
                  </span>
                </th>
                <th className={classNames("title", { "filter-applied": this.isFilterActive('title') })} scope="col" data-column-index="1" data-property="title">
                  <span className="published-table-heading">
                    {i18n.t("responseList.titleColHeading")}<span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                    <TextFilter
                      field={{ name: 'Response Title', property: 'title' }}
                      value={this.getFilterValue('title')}
                      onFilterApply={this.handleFilterApply}
                      onClear={this.handleFilterClear}
                    />
                  </span>
                </th>
                <th className={classNames("updatedDate", { "filter-applied": this.isFilterActive('dateModified') })} scope="col" data-column-index="2" data-property="dateModified">
                  <span className="published-table-heading">
                    {i18n.t("responseList.updatedDateColHeading")}<span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                    <DateFilter
                      field={{ name: 'Last Response Updated', property: 'dateModified' }}
                      firstFilterValue={this.getFilterValue('dateModified', 'ge')}
                      secondFilterValue={this.getFilterValue('dateModified', 'le')}
                      onFilterApply={this.handleFilterApply}
                      onClear={this.handleFilterClear}
                      showTime={false}
                    />
                  </span>
                </th>
                <th className={classNames("updatedBy", { "filter-applied": this.isFilterActive('lastUpdatedUserName') })} scope="col" data-column-index="3" data-property="lastUpdatedUserName">
                  <span className="published-table-heading">
                    {i18n.t("responseList.updatedByHeading")}<span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                    <TextFilter
                      field={{ name: 'Updated By', property: 'lastUpdatedUserName' }}
                      value={this.getFilterValue('lastUpdatedUserName')}
                      onFilterApply={this.handleFilterApply}
                      onClear={this.handleFilterClear}
                    />
                  </span>
                </th>
                <th className={classNames("category", { "filter-applied": this.isFilterActive('category/name') })} scope="col" data-column-index="4" data-property="category/name">
                  <span className="published-table-heading">
                    {i18n.t("responseList.categoryHeading")}<span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                    <SelectFilter
                      field={{ name: 'Category', property: 'category/name', multiSelection: true }}
                      value={this.getFilterValue('category/name')}
                      asyncloader={this.getCategoryOptions}
                      onFilterApply={this.handleFilterApply}
                      onClear={this.handleFilterClear}
                    />
                  </span>
                </th>
                <th className={classNames("panvivaId", { "filter-applied": this.isFilterActive('panvivaDocumentId') })} scope="col" data-column-index="5" data-property="panvivaDocumentId">
                  <span className="published-table-heading">
                    {i18n.t("responseList.panvivaIDHeading")}<span aria-hidden="true" className="sort-icon" onClick={this.handleClickHeader}></span>
                    <NumberFilter
                      field={{ name: 'Panviva ID', property: 'panvivaDocumentId' }}
                      firstFilterValue={this.getFilterValue('panvivaDocumentId', 'ge')}
                      secondFilterValue={this.getFilterValue('panvivaDocumentId', 'le')}
                      onFilterApply={this.handleFilterApply}
                      onClear={this.handleFilterClear}
                      className="panviva-id-filter"
                    />
                  </span>
                </th>
              </tr>
            }
          </thead>
          <tbody>
            {displayResults && displayResults.length > 0 ? (
              displayResults.map(item => renderListItem(item, this.props.isDraftList, this.handleVersionIconClick)
              )
            ) : showNoResults ? (
              <tr className="no-results-row">
                <td colSpan={this.props.isDraftList ? 4 : 7} className="no-results-cell">No results found</td>
              </tr>
            ) : (
              [...Array(numberOfEmptyRows)].map((item, i) => <SkeletonTableRow numOfColumns={this.props.isDraftList ? 4 : 7} key={i} />)
            )}
          </tbody>
        </table>

        {this.props.list && this.props.list.rowCount > 0 && (
          <Pagination
            onPageSizeChange={this.handlePageSizeChange}
            pageSize={this.props.list.pageSize}
            currentPage={this.props.list.currentPage}
            linkUrlLeftPart={this.props.baseRoute}
            totalRecordsNumber={this.props.list.rowCount}
            pageCount={this.props.list.pageCount}
            authState={this.props.authState}
          />
        )}

        {showEmptyModal && renderUpToDateModal(this.props.isDraftList)}

        {(this.props.authState && this.state.openModalState) && (
          <HistoricalResponseListModal
            responseId={this.state.responseId}
            documentId={this.state.documentId}
            authState={this.props.authState}
            onCloseIconClick={this.handleCloseIconClick}
          />
        )}

        <Tooltip
          id="version-history-tooltip"
          place={"bottom-start"}
          variant={"info"}
          className={"cc-tooltip"} />

      </div>
    );
  }

  public componentDidMount() {
    this.initSortingState();

    // Set default sorting by dateModified in descending order if no sorting is specified
    if (!this.props.orderBy && this.props.onSortChange) {
      this.props.onSortChange('dateModified desc');
    }
  }

  private initSortingState() {
    if (this.props.orderBy) {
      const table = document.getElementById('published-table');
      if (table) {
        const [property, direction] = this.props.orderBy.split(' ');
        const columnHeaders = [].slice.call(table.querySelectorAll('th'));

        for (let i = 0; i < columnHeaders.length; i++) {
          const ch = columnHeaders[i];
          const dataProperty = ch.getAttribute('data-property');

          if (dataProperty === property) {
            ch.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending');
            break;
          }
        }
      }
    }
  }

  private handlePageSizeChange(pageNumber: number, itemPerPage: number): any {
    this.props.handlePageSizeChange(
      pageNumber,
      itemPerPage,
      this.props.authState
    );
  }

  private sortOrderChanged(): any {
    let url = `${this.props.baseRoute}/${this.props.list.currentPage}`;
    if (
      this.props.list.sortOrder &&
      this.props.list.sortOrder.indexOf("-") > -1
    ) {
      url = `${url}?sortOrder=${responseListDefaultSort}`;
    } else {
      url = `${url}?sortOrder=-${responseListDefaultSort}`;
    }

    appHistory.push(url);
  }

  private handleVersionIconClick = (responseId: string, documentId: string) => {
    this.setState({
      openModalState: true,
      responseId: responseId,
      documentId: documentId
    });
  }

  private handleCloseIconClick = () => {
    this.setState({
      openModalState: false,
      responseId: "",
      documentId: ""
    });
  }
  private handleClickHeader(event: React.MouseEvent) {
    const target = event.currentTarget.closest('th');
    if (target && this.props.onSortChange) {
      const property = target.getAttribute('data-property');
      const currentSort = target.getAttribute('aria-sort');

      // Remove sort indicators from all other columns
      const table = document.getElementById('published-table');
      if (table) {
        const columnHeaders = [].slice.call(table.querySelectorAll('th'));
        for (let i = 0; i < columnHeaders.length; i++) {
          if (columnHeaders[i] !== target && columnHeaders[i].hasAttribute('aria-sort')) {
            columnHeaders[i].removeAttribute('aria-sort');
          }
        }
      }

      // Toggle sort direction
      let newDirection = 'desc';
      if (currentSort === 'descending') {
        target.setAttribute('aria-sort', 'ascending');
        newDirection = 'asc';
      } else {
        target.setAttribute('aria-sort', 'descending');
        newDirection = 'desc';
      }

      this.props.onSortChange(`${property} ${newDirection}`);
    }
  }
}

const renderUpToDateModal = (isDraft: boolean) =>
  !isDraft ? (
    <EmptyListModal
      title={i18n.t("responseList.emptyListHeadingMessage")}
      message={i18n.t("responseList.emptyListBodyMessage")}
    />
  ) : (
    <EmptyListModal
      title={i18n.t("draftResponseList.emptyListHeadingMessage")}
      message={i18n.t("draftResponseList.emptyListBodyMessage")}
    />
  );

const renderListItem = (item: Api.IResponseListViewModel, isDraft: boolean, onVersionIconClick: (responseId: string, docId: string) => void) => (
  <ResponseListRow response={item} key={item.id} isDraft={isDraft} iconClickCallback={() => onVersionIconClick(item.id, item.documentId)} />
);

const mapStateToProps = (state: IStoreState) => ({
  filters: state.responses.filters,
  filteredResults: state.responses.filteredResults
});

const mapDispatchToProps = {
  setFilter,
  clearFilter,
  clearAllFilters
};

export default connect(mapStateToProps, mapDispatchToProps)(Responses);
